import { format, parse } from "date-fns";
import { useMemo } from "react";
import { DateRange } from "react-day-picker";
import { cx } from "../../../utils/classnames";
import FilterCalendar from "./FilterCalendar";
import FilterPopover from "./FilterPopover";
import {
  getDatePresets,
  RESERVATION_FILTER_KEYS as KEYS,
  URL_DATE
} from "./reservationFilters";
import useFilterParams from "./useFilterParams";

const DATE_TYPES = [
  { travel: false, label: "Data e blerjes" },
  { travel: true, label: "Data e udhëtimit" }
];

const toDate = (value: string) => (value ? parse(value, URL_DATE, new Date()) : undefined);
const toLabel = (value: string) => format(parse(value, URL_DATE, new Date()), "dd.MM.yyyy");

interface FilterDatesProps {
  // Offer the purchase/travel switch. A list with no travel date (eSIM) turns it off and
  // filters by purchase date only.
  allowTravelDate?: boolean;
}

// Purchase or travel date, as a quick range or any range off the calendar. The chip names the
// preset when the range is one, so "7 ditët e fundit" reads as that rather than as two dates.
const FilterDates = ({ allowTravelDate = true }: FilterDatesProps) => {
  const { get, set } = useFilterParams();
  const isTravelDate = allowTravelDate && get(KEYS.dateType) === "travel";
  const dateFrom = get(KEYS.dateFrom);
  const dateTo = get(KEYS.dateTo);
  const presets = getDatePresets(isTravelDate);
  const activePreset = presets.find((p) => p.from === dateFrom && p.to === dateTo);

  const range = useMemo<DateRange | undefined>(
    () => (dateFrom || dateTo ? { from: toDate(dateFrom), to: toDate(dateTo) } : undefined),
    [dateFrom, dateTo]
  );

  // One picked day means that day only; the backend reads a date-only end as the whole day.
  const setRange = (from = "", to = from) => set({ [KEYS.dateFrom]: from, [KEYS.dateTo]: to });
  const onCalendar = (r: DateRange | undefined) =>
    setRange(r?.from ? format(r.from, URL_DATE) : "", r?.to ? format(r.to, URL_DATE) : undefined);

  let value = "";
  if (dateFrom) {
    const dates = activePreset?.label
      || (dateTo && dateTo !== dateFrom ? `${toLabel(dateFrom)} – ${toLabel(dateTo)}` : toLabel(dateFrom));
    value = `${isTravelDate ? "Udhëtimi" : "Blerja"}: ${dates}`;
  }

  return (
    <FilterPopover
      label="Data"
      value={value}
      onClear={() => set({ [KEYS.dateFrom]: "", [KEYS.dateTo]: "", [KEYS.dateType]: "" })}
      panelClassName="w-[calc(100vw-2rem)] sm:w-auto"
    >
      {(close) => (
        <div className="flex flex-col gap-3">
          {allowTravelDate && (
            <div className="flex rounded-lg bg-gray-100 p-1 text-sm" role="radiogroup">
              {DATE_TYPES.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  role="radio"
                  aria-checked={isTravelDate === option.travel}
                  onClick={() => set({ [KEYS.dateType]: option.travel ? "travel" : "" })}
                  className={cx([
                    "flex-1 px-3 py-1.5 rounded-md duration-200 whitespace-nowrap",
                    isTravelDate === option.travel
                      ? "bg-white text-black shadow-sm font-medium"
                      : "text-neutral hover:text-black"
                  ])}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <ul className="grid grid-cols-2 sm:flex sm:flex-col gap-1 sm:w-40 sm:border-r
              sm:border-gray-100 sm:pr-3"
            >
              {presets.map((preset) => (
                <li key={preset.label}>
                  <button
                    type="button"
                    onClick={() => {
                      setRange(preset.from, preset.to);
                      close();
                    }}
                    className={cx([
                      "w-full rounded-md px-2 py-1.5 text-left text-sm",
                      activePreset?.label === preset.label
                        ? "bg-primary/10 font-medium text-primary"
                        : "hover:bg-gray-50"
                    ])}
                  >
                    {preset.label}
                  </button>
                </li>
              ))}
            </ul>
            <FilterCalendar range={range} onSelect={onCalendar} />
          </div>
        </div>
      )}
    </FilterPopover>
  );
};

export default FilterDates;
